import { Injectable } from '@angular/core';

export type ImageResizePreset = 'small' | 'medium' | 'large';

export interface ImageResizeSettings {
  maxWidth: number;
  quality: number;
}

export const IMAGE_RESIZE_PRESETS: Record<ImageResizePreset, ImageResizeSettings> = {
  small: { maxWidth: 800, quality: 0.8 },
  medium: { maxWidth: 1600, quality: 0.85 },
  large: { maxWidth: 2560, quality: 0.9 },
};

export const DEFAULT_IMAGE_RESIZE_PRESET: ImageResizePreset = 'medium';

/**
 * Images that can safely be redrawn on a canvas. Animated formats (gif) and
 * vector formats (svg) are left untouched, since redrawing them loses content.
 */
const RESIZABLE_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

/**
 * Png stays png so that transparency survives - a transparent png drawn on a
 * jpeg canvas would get a black background. Everything else becomes jpeg.
 */
const OUTPUT_TYPES: Record<string, string> = {
  'image/jpeg': 'image/jpeg',
  'image/png': 'image/png',
  'image/webp': 'image/jpeg',
};

const FILE_EXTENSIONS: Record<string, string> = {
  'image/jpeg': 'jpg',
  'image/png': 'png',
};

@Injectable({
  providedIn: 'root',
})
export class ImageResizeService {

  canResize(file: File): boolean {
    return RESIZABLE_TYPES.includes(file.type.toLowerCase());
  }

  async resizeAll(files: File[], preset: ImageResizePreset = DEFAULT_IMAGE_RESIZE_PRESET): Promise<File[]> {
    return Promise.all(files.map((file) => this.resize(file, preset)));
  }

  /**
   * Scales an image down so its longest edge fits the preset. Returns the
   * original file untouched when it is not a resizable image, when resizing
   * would not make it smaller, or when the browser cannot do the work -
   * resizing must never be the reason an upload fails.
   */
  async resize(file: File, preset: ImageResizePreset = DEFAULT_IMAGE_RESIZE_PRESET): Promise<File> {
    if (!this.canResize(file)) {
      return file;
    }

    const { maxWidth, quality } = IMAGE_RESIZE_PRESETS[preset];
    const outputType = OUTPUT_TYPES[file.type.toLowerCase()];

    let bitmap: ImageBitmap | null = null;
    try {
      bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
      const scale = Math.min(1, maxWidth / Math.max(bitmap.width, bitmap.height));
      const width = Math.round(bitmap.width * scale);
      const height = Math.round(bitmap.height * scale);

      const blob = await this.draw(bitmap, width, height, outputType, quality);
      if (!blob || blob.size >= file.size) {
        return file;
      }

      return this.toFile(blob, file.name, outputType);
    } catch (error) {
      console.warn('Image resize failed, uploading the original file instead.', error);
      return file;
    } finally {
      bitmap?.close();
    }
  }

  private async draw(bitmap: ImageBitmap, width: number, height: number, type: string, quality: number): Promise<Blob | null> {
    if (typeof OffscreenCanvas !== 'undefined') {
      const canvas = new OffscreenCanvas(width, height);
      const context = canvas.getContext('2d');
      if (!context) {
        return null;
      }
      context.drawImage(bitmap, 0, 0, width, height);
      return canvas.convertToBlob({ type, quality });
    }

    // Safari below 16.4 has no OffscreenCanvas.convertToBlob.
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext('2d');
    if (!context) {
      return null;
    }
    context.drawImage(bitmap, 0, 0, width, height);
    return new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, type, quality));
  }

  private toFile(blob: Blob, originalName: string, type: string): File {
    return new File([blob], this.getFileName(originalName, type), {
      type,
      lastModified: Date.now(),
    });
  }

  private getFileName(originalName: string, type: string): string {
    const extension = FILE_EXTENSIONS[type];
    if (!extension) {
      return originalName;
    }

    const separatorIndex = originalName.lastIndexOf('.');
    const baseName = separatorIndex > 0 ? originalName.substring(0, separatorIndex) : originalName;
    return `${baseName}.${extension}`;
  }
}
